import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { getAutocompleteFctHelper } from '../../../core/autocomplete/helpers/autocomplete-function.helper';
import { ShAutocompleteService } from '../../../core/autocomplete/services/autocomplete.service';
import { AutocompleteFunction } from '../../../core/common/entities/autocompleteFunction.entity';
import { ExtendedBaseProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { ExtendedProfile } from '../../../core/profiles/entities/extended-profile.entity';
import { ProfileField, ProfileFieldScope } from '../../../core/profiles/entities/profile-field.entity';
import { Profile } from '../../../core/profiles/entities/profile.entity';
import { PartialRecord } from '../../../core/records/entities/partial-record.class';
import { RecordInheritance } from '../../../core/records/entities/record-inheritance.class';
import { ShProfileConfigService } from '../../services/profile-config.service';

@Component({
    selector: 'sh-profile-input-fieldset',
    templateUrl: './profile-input-fieldset.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShProfileInputFieldsetComponent implements OnDestroy, OnInit {
    public readonly profile$: ReplaySubject<ExtendedProfile> = new ReplaySubject<ExtendedProfile>(1);
    public profileFields$!: Observable<(ProfileField | ExtendedProfile)[]>;
    public profileName$!: Observable<string>;

    @Input() formGroup!: UntypedFormGroup;

    @Input() records: PartialRecord[] = [];

    @Input() recordInheritance?: RecordInheritance;
    @Input() aiProfile: Profile | undefined;

    @Input() isBatchEdit?: boolean = false;

    @Input() set profile(profile: ExtendedProfile) {
        if (profile) {
            this.profile$.next(profile);
        }
    }

    constructor(
        private readonly configService: ShProfileConfigService,
        private readonly autocompleteService: ShAutocompleteService
    ) {}

    ngOnInit(): void {
        this.initializeProfile();
    }

    ngOnDestroy(): void {
        this.profile$.complete();
    }

    public trackByIdentity = (index: number, item: ProfileField) => item.FieldDefinitionId;

    private initializeProfile(): void {
        const allProfileFields$: Observable<ExtendedBaseProfileField[]> = this.profile$.pipe(
            map(profile => profile.Fields)
        );
        this.profileFields$ = combineLatest([allProfileFields$, this.canViewStandard$, this.canViewAdvanced$]).pipe(
            map(([fields, standard, advanced]) => fields.filter(field => this.canViewField(field, standard, advanced)))
        );
    }

    /**
     * This function is called whenever a change happens on a field (for all fields) and returns wether that field should be hidden.
     * This can be used to perform live logic to hide a field based on the value of another field.
     *
     * Example: Hide Mh2Reuse input field when Mh2Gdpr value is 'Disallowed'
     *
     * This logic should be implemented in the ProfileConfig
     *
     * @param field ExtendedProfileField to perform check for
     * @returns wether or not a field should be hidden
     */
    public isVisuallyHidden(field: ExtendedBaseProfileField): boolean {
        return this.configService.isVisuallyHidden(field, this.formGroup);
    }

    public getAutocompleteFct(profileField: ExtendedBaseProfileField): AutocompleteFunction | undefined {
        const fieldDefinitionDottedKey: string | null = this.autocompleteService.getAutocompleteFieldKey(
            profileField.FieldDefinition
        );

        if (!fieldDefinitionDottedKey) {
            return undefined;
        }

        return getAutocompleteFctHelper(fieldDefinitionDottedKey, this.autocompleteService, false);
    }

    public getSubFieldAutocompleteFcts(profileField: ExtendedBaseProfileField): Record<string, AutocompleteFunction> {
        const subFieldAutocompleteFcts: Record<string, AutocompleteFunction> = {};
        if (profileField.FieldDefinition.Children) {
            profileField.FieldDefinition.Children.forEach(childField => {
                const fieldDefinitionDottedKey: string | null =
                    this.autocompleteService.getAutocompleteFieldKey(childField);

                if (!fieldDefinitionDottedKey) {
                    return;
                }

                subFieldAutocompleteFcts[childField.Key] = getAutocompleteFctHelper(
                    fieldDefinitionDottedKey,
                    this.autocompleteService,
                    false
                );
            });
        }

        return subFieldAutocompleteFcts;
    }

    public canViewField(field: ExtendedBaseProfileField, canViewStandard: boolean, canViewAdvanced: boolean): boolean {
        if (field.Scope === ProfileFieldScope.Standard) {
            return canViewStandard;
        }

        if (field.Scope === ProfileFieldScope.Advanced) {
            return canViewAdvanced;
        }

        return true;
    }

    public get canViewStandard$(): Observable<boolean> {
        return this.configService.canViewStandard();
    }

    public get canEditStandard$(): Observable<boolean> {
        return this.configService.canEditStandard();
    }

    public get canViewAdvanced$(): Observable<boolean> {
        return this.configService.canViewAdvanced();
    }

    public get canEditAdvanced$(): Observable<boolean> {
        return this.configService.canEditAdvanced();
    }
}
